<script setup lang="ts">
import { formatDate } from '~/shared/utils/formatDate';
import { resizeImage, resizeImageSrcsetWidths } from '~/shared/lib/resizeImage';
import { NEWS_ITEM_CARD_IMAGE_WIDTH } from './lib/const';

const props = defineProps<{
  title: string;
  category?: string | false;
  categoryLink?: string | false;
  date?: string;
  subject?: string;
  link: string;
  views?: number;
  image?: string;
  newTab?: boolean;
}>();

/* Ширина зависит от вьюпорта, поэтому w-дескрипторы плюс sizes. */
const imageSrc = computed(() =>
  resizeImage(props.image, { width: NEWS_ITEM_CARD_IMAGE_WIDTH }),
);

const imageSrcset = computed(() =>
  resizeImageSrcsetWidths(props.image, [320, 480, 640, 960]),
);
</script>

<template>
  <div :class="$style.item">
    <div :class="$style.item__info">
      <div :class="$style.item__wrapper">
        <NuxtLink
          v-if="category && categoryLink"
          :to="categoryLink"
          :class="$style.item__project"
        >
          {{ props.category }}
        </NuxtLink>
        <NuxtLink
          :to="link"
          :class="$style.item__title"
          :target="newTab ? '_blank' : '_self'"
          :external="newTab"
        >
          {{ props.title }}
        </NuxtLink>
        <div
          v-if="props.subject"
          :class="$style.item__text"
          v-html="props.subject"
        />
      </div>
      <div v-if="props.date" :class="$style.item__date">
        {{ formatDate('fullDate', props.date) }}
      </div>
    </div>
    <NuxtLink
      v-if="image"
      :to="link"
      :class="$style.item__photoWrapper"
      :target="newTab ? '_blank' : '_self'"
      :external="newTab"
    >
      <img
        :src="imageSrc"
        :srcset="imageSrcset || undefined"
        sizes="(max-width: 767px) 100vw, 320px"
        :width="NEWS_ITEM_CARD_IMAGE_WIDTH"
        :class="$style.item__photo"
        alt=""
      />
    </NuxtLink>
  </div>
</template>

<style module lang="scss">
.item {
  display: flex;
  flex-flow: row nowrap;
  padding: var(--space-2-5x);
  width: 100%;
  gap: var(--space-5x);
  border-bottom: 1px solid var(--color-gray-line);
  justify-content: space-between;

  &__info {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__wrapper {
    display: flex;
    flex-flow: column nowrap;
    gap: var(--space-2x);
  }

  &__project {
    @include text_caption2_caps;
    color: var(--color-gray-text);
    text-decoration: none;
  }

  &__title {
    @include text_h5;
    color: var(--color-black1);
    text-decoration: none;
  }

  &__text {
    @include text_body2;
    color: var(--color-black1);
  }

  &__date {
    display: flex;
    align-items: center;
    @include text_caption1_caps;
    color: var(--color-gray-text);
    gap: 20px;
    margin-top: var(--space-2x);
  }

  &__photo {
    width: 320px;
    height: auto;
    flex-shrink: 0;

    &Wrapper {
      display: flex;
      height: 100%;
    }
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (max-width: 1379px) {
  .item {
    gap: 0;
    &__wrapper {
      gap: var(--space-1x);
    }

    &__title {
      @include text_body1_large_medium;
    }

    &__text {
      @include text_body2;
    }
  }
}

@media (max-width: 767px) {
  .item {
    flex-direction: column;
    gap: var(--space-2-5x);
    border-bottom: 1px solid var(--color-gray-line);
    &__wrapper {
      margin-bottom: var(--space-1x);
    }

    &__photo {
      width: 100%;
      height: 100%;
    }
  }
}

@media (max-width: 479px) {
  .item {
    padding: var(--space-2x);
  }
}
</style>
